import React, { useCallback, useEffect, useRef, useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { FaLink } from "react-icons/fa";
import Particle from "../Particle";
import PageSeo from "../SEO/PageSeo";
import { getSortedPortfolio, portfolioFileUrl } from "../../config/portfolioData";
import { useLanguage } from "../../context/LanguageContext";

async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

function PortfolioPage() {
  const { t } = useLanguage();
  const projects = getSortedPortfolio();
  const [copiedId, setCopiedId] = useState(null);
  const copiedTimer = useRef(null);

  const copyProjectLink = useCallback((projectId) => {
    const url = `${window.location.origin}/portfolio#${encodeURIComponent(projectId)}`;
    copyTextToClipboard(url).then((ok) => {
      if (!ok) return;
      setCopiedId(projectId);
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopiedId(null), 2000);
    });
  }, []);

  useEffect(
    () => () => {
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    },
    []
  );

  return (
    <Container fluid className="project-section broxe-portfolio-page" id="portfolio-page">
      <PageSeo
        title={t("seo.portfolio.title")}
        description={t("seo.portfolio.description")}
        breadcrumbItems={[
          { name: t("nav.home"), path: "/" },
          { name: t("nav.portfolio"), path: "/portfolio" },
        ]}
      />
      <Particle />
      <Container className="pb-5" style={{ paddingTop: "96px" }}>
        <Row className="mb-4 text-center">
          <Col lg={8} className="mx-auto">
            <h1 className="project-heading mb-2">{t("portfolio.pageTitle")}</h1>
            <p className="text-white-50">{t("portfolio.pageSubtitle")}</p>
          </Col>
        </Row>

        {projects.map((project) => {
          const title = t(project.titleKey);
          const desc = project.descKey ? t(project.descKey) : "";

          return (
            <section
              key={project.id}
              id={project.id}
              className="broxe-portfolio-block mb-5 pb-4 border-bottom border-secondary border-opacity-25"
            >
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
                <h2 className="h4 text-white mb-0">{title}</h2>
                <Button
                  type="button"
                  variant="outline-light"
                  size="sm"
                  className="broxe-portfolio-copy-link flex-shrink-0"
                  onClick={() => copyProjectLink(project.id)}
                  aria-label={t("portfolio.copyLink")}
                >
                  <FaLink className="me-1" aria-hidden />
                  {copiedId === project.id ? t("portfolio.linkCopied") : t("portfolio.copyLink")}
                </Button>
              </div>
              {desc ? <p className="text-white-50 small mb-3">{desc}</p> : null}

              {project.kind === "video" ? (
                <div className="ratio ratio-16x9 broxe-portfolio-page-video-wrap">
                  <video
                    className="rounded"
                    src={portfolioFileUrl(project.file)}
                    controls
                    playsInline
                    preload="metadata"
                    title={title}
                  />
                </div>
              ) : (
                <Row className="g-3">
                  {project.images.map((img) => (
                    <Col key={img} xs={12} sm={6} lg={4}>
                      <a
                        href={portfolioFileUrl(project.folder, img)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="broxe-portfolio-lightbox d-block"
                      >
                        <img
                          className="img-fluid rounded broxe-portfolio-gallery-img w-100"
                          src={portfolioFileUrl(project.folder, img)}
                          alt={`${title} — ${img}`}
                          loading="lazy"
                          decoding="async"
                        />
                      </a>
                    </Col>
                  ))}
                </Row>
              )}
            </section>
          );
        })}
      </Container>
    </Container>
  );
}

export default PortfolioPage;
